import { useCallback } from "react";
import { Scene } from "@babylonjs/core/scene";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MAP_MESH_SIZE, BUTTON_ASSET_SIZE } from "common/model";
import { FORWARD_WORLD, GameState } from "./types";
import { probeFront, probeButtonAt, calcOffset, RAY_DISTANCE_FORWARD } from "../ray";
import {
  performCharStep,
  performBoxSlide,
  performCharBump,
  performCharFall,
  performBoxFall,
} from "../../animations";

type Deps = {
  stateRef: React.MutableRefObject<GameState | null>;
  sceneRef: React.MutableRefObject<Scene | null>;
  speedRef: React.MutableRefObject<number>;
  success: boolean;
  setAnimating: (v: boolean) => void;
  resetCharToStart: () => void;
  tryTriggerClear: () => void;
  moveSound: (rate?: number) => void;
  failureSound: (rate?: number) => void;
  fallingSound: (rate?: number) => void;
};

export const useHandleForward = (deps: Deps) => {
  const {
    stateRef,
    sceneRef,
    speedRef,
    success,
    setAnimating,
    resetCharToStart,
    tryTriggerClear,
    moveSound,
    failureSound,
    fallingSound,
  } = deps;

  return useCallback(async () => {
    const g = stateRef.current;
    const scene = sceneRef.current;
    if (!g || !scene || !g.charMesh || g.isAnimating || success) return;
    const char = g.charMesh;
    const fwd = FORWARD_WORLD[g.charDir];
    const speed = speedRef.current;

    // 現在ボタン上にいるかを判定 → 実効床面 Y を補正
    const currentBtn = probeButtonAt(scene, char.position.x, char.position.y, char.position.z, 1);
    const effectiveCharY = char.position.y - (currentBtn ? BUTTON_ASSET_SIZE.y : 0);

    // 前 1 マス（distance 2.5: drop / wall は hit しない）
    const probe1 = probeFront(scene, char.position, fwd, effectiveCharY, RAY_DISTANCE_FORWARD);

    // 前マスのボタン有無 → offset
    const target1X = char.position.x + fwd.dx * MAP_MESH_SIZE;
    const target1Z = char.position.z + fwd.dz * MAP_MESH_SIZE;
    const nextBtn1 = probeButtonAt(scene, target1X, char.position.y + 5, target1Z, 15);
    const offset1 = calcOffset(currentBtn, nextBtn1);

    g.isAnimating = true;
    setAnimating(true);
    try {
      switch (probe1.kind) {
        case "floor": {
          const charTarget = {
            x: target1X,
            y: char.position.y + offset1,
            z: target1Z,
          };
          moveSound(speed);
          await performCharStep(scene, char, charTarget, speed);
          return;
        }

        case "step":
        case "drop":
        case "wall":
          failureSound(speed);
          await performCharBump(scene, char, fwd, speed);
          return;

        case "void":
          fallingSound(speed);
          await performCharFall(scene, char, fwd, undefined, speed);
          resetCharToStart();
          return;

        case "river":
          fallingSound(speed);
          await performCharFall(scene, char, fwd, { stopAtY: probe1.topY }, speed);
          resetCharToStart();
          return;

        case "box": {
          // 箱の前方を判定（箱の position から下向き ray）
          const box = probe1.hitMesh as AbstractMesh;
          const boxCurrentBtn = probeButtonAt(
            scene,
            box.position.x,
            box.position.y,
            box.position.z,
            1,
          );
          const effectiveBoxY = box.position.y - (boxCurrentBtn ? BUTTON_ASSET_SIZE.y : 0);
          const probe2 = probeFront(scene, box.position, fwd, effectiveBoxY, RAY_DISTANCE_FORWARD);

          if (
            probe2.kind === "step" ||
            probe2.kind === "drop" ||
            probe2.kind === "wall" ||
            probe2.kind === "box"
          ) {
            failureSound(speed);
            await performCharBump(scene, char, fwd, speed);
            return;
          }

          if (probe2.kind === "void") {
            fallingSound(speed);
            await performBoxFall(scene, box, fwd, undefined, speed);
            return;
          }

          if (probe2.kind === "river") {
            fallingSound(speed);
            await performBoxFall(scene, box, fwd, { stopAtY: probe2.topY }, speed);
            return;
          }

          // floor: 箱とキャラ同時前進
          const target2X = box.position.x + fwd.dx * MAP_MESH_SIZE;
          const target2Z = box.position.z + fwd.dz * MAP_MESH_SIZE;
          const boxNextBtn = probeButtonAt(scene, target2X, char.position.y + 5, target2Z, 15);
          const offsetBox = calcOffset(boxCurrentBtn, boxNextBtn);
          const boxTarget = {
            x: target2X,
            y: box.position.y + offsetBox,
            z: target2Z,
          };
          const charTarget = {
            x: target1X,
            y: char.position.y + offset1,
            z: target1Z,
          };
          moveSound(speed);
          await Promise.all([
            performBoxSlide(scene, box, boxTarget, speed),
            performCharStep(scene, char, charTarget, speed),
          ]);
          return;
        }
      }
    } finally {
      g.isAnimating = false;
      setAnimating(false);
      tryTriggerClear();
    }
  }, [
    stateRef,
    sceneRef,
    speedRef,
    success,
    setAnimating,
    resetCharToStart,
    tryTriggerClear,
    moveSound,
    failureSound,
    fallingSound,
  ]);
};
